/**
 * Angle handling.
 *
 * Original (telnet humans): 0° = top, clockwise positive.
 * Internal physics (initShot): mathematical — 0° = +X (right), CCW,
 * with vy = v * -sin(θ) matching the original C code.
 *
 * Conversion (network.c): internal = -ui + 90
 *
 * UI precision: 0.00001° (integer 0.01 milli-degrees).
 */

export const ANGLE_UNIT = 1e-5; // degrees
export const FULL_CIRCLE_UNITS = 360 / ANGLE_UNIT; // 36_000_000
/** Shift+arrow step: 5° in angle units. */
export const SHIFT_STEP_UNITS = 5 / ANGLE_UNIT; // 500_000

/** Normalize UI angle to [0, 360). */
export function normalizeUiDegrees(deg: number): number {
  let d = deg % 360;
  if (d < 0) d += 360;
  if (d >= 360 - 1e-12) d = 0;
  return d;
}

export function unitsToUiDegrees(units: number): number {
  const u = ((units % FULL_CIRCLE_UNITS) + FULL_CIRCLE_UNITS) % FULL_CIRCLE_UNITS;
  return u * ANGLE_UNIT;
}

export function uiDegreesToUnits(deg: number): number {
  const n = normalizeUiDegrees(deg);
  let u = Math.round(n / ANGLE_UNIT);
  u = ((u % FULL_CIRCLE_UNITS) + FULL_CIRCLE_UNITS) % FULL_CIRCLE_UNITS;
  return u;
}

export function uiToInternal(uiDeg: number): number {
  return -uiDeg + 90;
}

export function internalToUi(internalDeg: number): number {
  return normalizeUiDegrees(-(internalDeg - 90));
}

export function velocityFromInternal(internalDeg: number, speed: number): { x: number; y: number } {
  const rad = (internalDeg / 180) * Math.PI;
  return {
    x: speed * Math.cos(rad),
    y: speed * -Math.sin(rad),
  };
}

/** Format with exactly 5 decimal places. */
export function formatAngleDisplay(units: number, decimalSep: '.' | ',' = '.'): string {
  const deg = unitsToUiDegrees(units);
  const raw = deg.toFixed(5);
  return decimalSep === ',' ? raw.replace('.', ',') : raw;
}

/** Digit places: 0 = 1°, …, 5 = 0.00001°. Values are in ANGLE_UNIT counts. */
export const ANGLE_DIGIT_STEPS = [
  100_000, // 1°
  10_000, // 0.1°
  1_000, // 0.01°
  100, // 0.001°
  10, // 0.0001°
  1, // 0.00001°
] as const;

export type AngleDigitIndex = 0 | 1 | 2 | 3 | 4 | 5;
